"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Thermometer, ShieldCheck, Bot } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfe Smart-Home-Kacheln im Vordergrund.
 */
export default function Preview() {
  const [activeRoom, setActiveRoom] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveRoom((prev) => (prev + 1) % 3);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  const rooms = [
    {
      name: "Licht An",
      value: "Warm 80%",
      Icon: Lightbulb,
      glow: "border-amber-400/80 shadow-[0_14px_30px_-4px_rgba(245,158,11,0.6)]",
      iconColor: "text-amber-500",
    },
    {
      name: "Heizung",
      value: "21,5 °C",
      Icon: Thermometer,
      glow: "border-purple-400/80 shadow-[0_14px_30px_-4px_rgba(168,85,247,0.6)]",
      iconColor: "text-purple-500",
    },
    {
      name: "Haustür",
      value: "Verriegelt ✓",
      Icon: ShieldCheck,
      glow: "border-emerald-400/80 shadow-[0_14px_30px_-4px_rgba(16,185,129,0.6)]",
      iconColor: "text-emerald-500",
    },
  ];

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 4, repeat: Infinity }}
        className={`pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl transition-colors duration-700 ${
          activeRoom === 0
            ? "bg-amber-500/35"
            : activeRoom === 1
              ? "bg-purple-500/35"
              : "bg-emerald-500/35"
        }`}
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex flex-col items-center gap-3 antialiased">
        <div className="flex items-center gap-1.5 rounded-full border border-purple-400/70 bg-white px-3 py-1 text-[11px] font-black text-neutral-900 shadow-lg dark:bg-zinc-900 dark:text-white">
          <Bot className="h-3.5 w-3.5 text-purple-500" />
          <span>Smart-Home Agent</span>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {rooms.map((room, idx) => {
            const isActive = idx === activeRoom;
            const Icon = room.Icon;
            return (
              <motion.div
                key={room.name}
                animate={{ y: isActive ? -6 : 2 }}
                transition={{ type: "spring", stiffness: 280, damping: 20 }}
                className={`flex flex-col items-center gap-1 rounded-2xl border bg-white px-3 py-2.5 transition-all duration-300 dark:bg-zinc-900 ${
                  isActive
                    ? room.glow
                    : "border-black/10 opacity-65 dark:border-white/10"
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${
                    isActive ? room.iconColor : "text-neutral-400"
                  }`}
                />
                <span className="font-sans text-[11px] font-extrabold text-neutral-900 dark:text-white">
                  {room.name}
                </span>
                <span className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400">
                  {room.value}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
